import { Box, Stack, Typography } from "@mui/material";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { useState, type FC } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useCallCenterShift } from "../../contexts/CallCenterShiftProvider";
import { CC_BRAND } from "./call-center-brand";
import { TOUR_FAQ } from "./call-center-tour-fixtures";
import { CcButton, CcEyebrow, CcPanel } from "./CallCenterUi";
import { CC } from "./call-center-ui.theme";

export const CallCenterHelp: FC<{ example?: boolean }> = ({ example = false }) => {
  const { setTourOpen, setTab } = useCallCenterShift();
  const { setAlertText, setSeverity } = useAlert();
  const [calling, setCalling] = useState(false);
  const faqQuery = useQuery({
    queryKey: ["call-center-settings"],
    enabled: !example,
    queryFn: async () => {
      const { data } = await linko.callCenter.queries.getSettings();
      return data;
    },
  });

  const faq = example
    ? TOUR_FAQ
    : (faqQuery.data?.faq ?? []).filter((item) => item.isActive);

  return (
    <Stack
      gap={2.5}
      sx={{ p: { xs: 1.5, md: 2.5 }, overflow: "auto", flex: 1 }}
      data-tour="cc-help-panel"
    >
      <Stack gap={0.75}>
        <CcEyebrow>{CC_BRAND}</CcEyebrow>
        <Typography
          sx={{
            fontFamily: CC.font,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: "-0.03em",
            color: CC.ink,
          }}
        >
          עזרה למשמרת
        </Typography>
        <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, lineHeight: 1.6 }}>
          בשיחה נכנסת שואלים אם מגיעים לבד (פרטני) או כחלק מקבוצה, ואז מחפשים במסלול המתאים.
        </Typography>
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap">
        <CcButton
          data-tour="cc-help-tour"
          onClick={() => {
            if (example) return;
            setTab("task");
            setTourOpen(true);
          }}
        >
          סיור מונחה
        </CcButton>
        <CcButton
          data-tour="cc-help-call"
          kind="ghost"
          disabled={calling}
          onClick={async () => {
            if (example || calling) return;
            setCalling(true);
            try {
              const { data } = await linko.callCenter.mutations.callCoordinator();
              setSeverity("info");
              setAlertText(
                data.alreadyOpen ? "הקריאה כבר אצל המנהלת" : "נשלחה קריאה למנהלת",
              );
            } catch {
              setSeverity("error");
              setAlertText("לא ניתן לקרוא למנהלת");
            } finally {
              setCalling(false);
            }
          }}
        >
          קריאה למנהלת
        </CcButton>
        <CcButton
          data-tour="cc-help-chat"
          kind="soft"
          onClick={() => {
            if (example) return;
            setTab("chat");
          }}
        >
          העבר למנהלת בצ'אט
        </CcButton>
      </Stack>

      <Stack gap={1.25} data-tour="cc-help-faq" sx={{ minHeight: 80 }}>
        {faq.map((item, index) => (
          <CcPanel key={item.id} sx={{ p: 2.25 }}>
            <Stack direction="row" gap={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 99,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(5,32,76,0.06)",
                  fontFamily: CC.font,
                  fontSize: 12,
                  fontWeight: 700,
                  color: CC.inkMuted,
                }}
              >
                {String(index + 1).padStart(2, "0")}
              </Box>
              <Box>
                <Typography sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
                  {item.question}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: CC.font,
                    whiteSpace: "pre-wrap",
                    mt: 0.75,
                    color: CC.inkMuted,
                    lineHeight: 1.7,
                  }}
                >
                  {item.answer}
                </Typography>
              </Box>
            </Stack>
          </CcPanel>
        ))}
      </Stack>
    </Stack>
  );
};
